<template>
  <div class="v5-srv">
    <router-link v-if="lobbyLink" class="v5-btn" :to="lobbyLink">
      <PlayCircleIcon class="i" aria-hidden="true" />{{ t('v5.shell.go_to_match') }}
    </router-link>
    <div v-else-if="user.can_use_matchmaking && queue.isQueued.value" class="v5-srv__queue" role="status">
      <router-link class="v5-btn" :to="matchmakingLink">
        <span class="v5-spin" aria-hidden="true" />{{ t('v5.shell.searching') }}
        <span class="num">{{ queue.elapsedLabel.value }}</span>
      </router-link>
      <button v-if="queue.isOwner.value" class="v5-ib" type="button" :aria-label="t('v5.shell.cancel_search')" @click="queue.cancelMatchmaking">
        <XMarkIcon class="i" aria-hidden="true" />
      </button>
    </div>

    <template v-if="canStartServer">
      <router-link v-if="!server?.id" class="v5-btn-p" :to="playLink">
        <PlayIcon class="i" aria-hidden="true" />{{ t('v5.shell.play_now') }}
      </router-link>
      <template v-else-if="server.status && server.status !== 'online'">
        <span class="v5-srv__boot" role="status"><span class="v5-spin" aria-hidden="true" />{{ t('v5.shell.server_starting') }}</span>
        <button class="v5-ib" type="button" :aria-label="t('v5.shell.cancel_server')" :disabled="terminating" @click="terminate">
          <XMarkIcon class="i" aria-hidden="true" />
        </button>
      </template>
      <template v-else>
        <button class="v5-conn v5-srv__wide" type="button" :aria-label="t('v5.shell.copy_connect')" @click="copy(server)">
          <span class="num">connect {{ server.ip }}:{{ server.port }}; password <span :class="{ 'v5-blur': user.streamer_mode }">{{ server.password }}</span></span>
          <CheckIcon v-if="copied === server.id" class="i v5-good" aria-hidden="true" />
          <ClipboardDocumentIcon v-else class="i" aria-hidden="true" />
        </button>
        <a class="v5-ib v5-srv__wide" :href="steamLink(server)" :aria-label="t('v5.shell.open_game')">
          <PlayIcon class="i" aria-hidden="true" />
        </a>
        <button class="v5-ib v5-srv__wide" type="button" :aria-label="t('v5.shell.terminate_server')" :disabled="terminating" @click="terminate">
          <XMarkIcon class="i" aria-hidden="true" />
        </button>
        <router-link class="v5-btn" :to="managerLink">{{ t('v5.shell.manage') }}</router-link>
      </template>

      <div v-if="teamServers.length && user.access_level > 2" ref="root" class="v5-pop-wrap">
        <button
          class="v5-ib"
          type="button"
          :aria-label="t('v5.shell.team_servers_n', { n: teamServers.length })"
          :aria-expanded="String(open)"
          aria-controls="v5-team-servers"
          @click="toggle"
        >
          <ServerStackIcon class="i" aria-hidden="true" /><span class="num">{{ teamServers.length }}</span>
        </button>
        <div v-if="open" id="v5-team-servers" class="v5-pop v5-pop--wide">
          <div class="v5-pop__head"><h2>{{ t('v5.shell.team_servers') }}</h2></div>
          <ul class="v5-pop__rows">
            <li v-for="s in teamServers" :key="s.id">
              <img v-if="s.started_by?.steam_profile?.avatarfull" class="v5-avatar" :src="s.started_by.steam_profile.avatarfull" alt="">
              <span class="v5-pop__tx">
                <b>{{ t('v5.shell.started_by', { name: s.started_by?.steam_profile?.personaname }) }}</b>
                <span class="num">connect {{ s.ip }}:{{ s.port }}; password <span :class="{ 'v5-blur': user.streamer_mode }">{{ s.password }}</span></span>
              </span>
              <button class="v5-ib" type="button" :aria-label="t('v5.shell.copy_connect')" @click="copy(s)">
                <CheckIcon v-if="copied === s.id" class="i v5-good" aria-hidden="true" />
                <ClipboardDocumentIcon v-else class="i" aria-hidden="true" />
              </button>
              <a class="v5-ib" :href="steamLink(s)" :aria-label="t('v5.shell.join_named', { name: s.started_by?.steam_profile?.personaname })">
                <PlayIcon class="i" aria-hidden="true" />
              </a>
            </li>
          </ul>
        </div>
      </div>
    </template>

    <span v-else-if="launcherDisabled" class="v5-srv__maint">
      <button class="v5-btn" type="button" disabled aria-describedby="v5-maint">
        <LockClosedIcon class="i" aria-hidden="true" />{{ t('v5.shell.under_maintenance') }}
      </button>
      <span id="v5-maint" role="tooltip">{{ t('v5.shell.maintenance_hint') }}</span>
    </span>
    <button v-else class="v5-btn-p" type="button" @click="emit('subscribe')">
      <PlayIcon class="i" aria-hidden="true" />{{ t('v5.shell.play_now') }}
    </button>
  </div>
</template>

<script setup>
import {
  computed, onBeforeUnmount, ref, watch,
} from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';
import {
  CheckIcon, ClipboardDocumentIcon, LockClosedIcon, PlayCircleIcon, PlayIcon, ServerStackIcon,
  XMarkIcon,
} from '@heroicons/vue/24/outline';
import usePopover from './usePopover';
import { useQueueSearch } from '../../composables/useMatchmakingQueue';
import { v5OrClassic } from '../nav';

// The classic NavBar's server corner: matchmaking search, Play Now, a starting or running
// server, the team's servers, maintenance, and the subscribe prompt for free users.
const props = defineProps({
  user: { type: Object, required: true },
  server: { type: Object, default: null },
  teamServers: { type: Array, default: () => [] },
  canStartServer: { type: Boolean, default: false },
  launcherDisabled: { type: Boolean, default: false },
  terminateServer: { type: Function, required: true },
});
const emit = defineEmits(['subscribe']);
const { t } = useI18n();
const store = useStore();
const route = useRoute();
const router = useRouter();
const { open, root, toggle } = usePopover();
const queue = useQueueSearch();

const playLink = computed(() => v5OrClassic(router, '/v5/play', '/play'));
const managerLink = computed(() => v5OrClassic(router, '/v5/server_manager', '/server_manager'));
const matchmakingLink = computed(() => v5OrClassic(router, '/v5/matchmaking', '/matchmaking'));
const lobbyLink = computed(() => {
  const lobby = queue.activeLobby.value;
  if (!props.user.can_use_matchmaking || !lobby || route.params.id === lobby.uuid) return null;
  return v5OrClassic(router, `/v5/matchmaking/lobby/${lobby.uuid}`, `/matchmaking/lobby/${lobby.uuid}`);
});

const steamLink = (s) => `steam://connect/${s.raw_ip}:${s.port}/${s.password}`;

const copied = ref(null);
let copiedTimer;
function copy(s) {
  navigator.clipboard?.writeText(`connect ${s.ip}:${s.port}; password ${s.password}`).then(() => {
    copied.value = s.id;
    clearTimeout(copiedTimer);
    copiedTimer = setTimeout(() => { copied.value = null; }, 1200);
  }).catch(() => {});
}
onBeforeUnmount(() => clearTimeout(copiedTimer));

const terminating = ref(false);
watch(() => props.server?.id, () => { terminating.value = false; });
function terminate() {
  // eslint-disable-next-line no-alert
  if (terminating.value || !window.confirm(t('v5.shell.confirm_terminate'))) return;
  terminating.value = true;
  // The server goes when CsServerChannel says so; until then the button stays busy
  props.terminateServer(props.server.id).catch(() => {
    terminating.value = false;
    store.commit('showMessage', { messageType: 'error', messageText: t('v5.shell.terminate_failed') });
  });
}
</script>
